import {
  cloneObject,
  deserify,
  IconWrapper,
  IStyles,
  pxToRem,
  pxTovW,
  QuantityButtonV2,
  SelectMenu,
} from '@geneo2-web/shared-ui';
import { Box, Checkbox, Divider, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { Fragment } from 'react';
import { Question_QuestionEnum } from '@protos/content_management/content.db_pb';
import {
  CloudUpload,
  MultiSubjectSubSection,
  QpgSubsectionTypeEnum,
  QuestionSection,
  QuestionTypeMarks,
  SectionQuestionMetaData,
} from '@protos/learning_management/lms.db_pb';
import { useAppSelector } from '../../../../../reduxStore/reduxHooks';
import { QuestionTypeCount } from '@protos/learning_management/lms.hw.teacher.apis_pb';
import { useLocation } from 'react-router-dom';
import { useDownloadContext } from '../../../../../app/Context/DownloadContextProviderV2';
import { useDispatch } from 'react-redux';
import {
  setQPGContent,
  setQPGSettingsConfig,
  setQPGSubsectionData,
} from '../../../reducer/assessmentFlow.slice';
import {
  SubSectionQuestionType,
  SubSectionTypeV2,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import {
  areAllSubSectionQuestionsFilled,
  getRemainingForSubSection,
  getRemainingForSubSectionAIMode,
  isPassageSectionEnabled,
  getAllPassageModuleIdsFromChapters,
  getEffectiveMarksForSubSectionQuestion,
  getQpgCreationSubSectionMarksTotal,
  toRequestMarks,
  formatMarksDisplay,
  withSeededGroupedQuestionHeading,
} from '../../../utils/QPGv2.utils';
import { QPGPassageSectionPanel } from './QPGPassageSectionPanel';

const getSubSectionQuestionTotal = (questions: SubSectionQuestionType[]) =>
  questions.reduce((sum, question) => {
    const count = question.noOfQuestion ?? 0;
    if (count <= 0) return sum;
    return sum + (question.isGrouped ? 1 : count);
  }, 0);

/**
 * Attempt any must stay in [1, questionTotal - 1].
 * Fewer than 2 questions cannot use attempt any, so the flag is cleared.
 */
const clampAttemptAnyCount = (
  count: number,
  questionTotal: number
): number | undefined => {
  const max = questionTotal - 1;
  if (max < 1) return undefined;
  return Math.min(Math.max(1, count), max);
};

/** Clamp per-row group attempt-any to [1, noOfQuestion - 1]. */
const clampGroupQuestionAttemptedAny = (
  count: number,
  noOfQuestion: number
): number | undefined => {
  const max = noOfQuestion - 1;
  if (max < 1) return undefined;
  return Math.min(Math.max(1, count), max);
};

/** Rebuild a subsection without optional attemptAnyCount (protobuf-es skips undefined). */
const rebuildSubSectionWithoutAttemptAny = (
  sub: SubSectionTypeV2,
  subSectionQuestions: SubSectionQuestionType[]
): SubSectionTypeV2 =>
  new SubSectionTypeV2({
    subSectionName: sub.subSectionName,
    sectionNumber: sub.sectionNumber,
    subSectionQuestions,
    passageText: sub.passageText,
    passageImagesUpload: sub.passageImagesUpload,
    passageModuleIds: sub.passageModuleIds ?? [],
    ...(sub.subSectionType !== undefined
      ? { subSectionType: sub.subSectionType }
      : {}),
    ...(sub.isAiGeneratedPassage !== undefined
      ? { isAiGeneratedPassage: sub.isAiGeneratedPassage }
      : {}),
  });

const withSubsectionAttemptAnyCount = (
  sub: SubSectionTypeV2,
  subSectionQuestions: SubSectionQuestionType[],
  attemptAnyCount: number | undefined
): SubSectionTypeV2 => {
  if (attemptAnyCount === undefined) {
    return rebuildSubSectionWithoutAttemptAny(sub, subSectionQuestions);
  }
  return new SubSectionTypeV2({
    ...sub,
    subSectionQuestions,
    attemptAnyCount,
  });
};

/** Rebuild content subsection without optional attemptAnyCount. */
const rebuildContentSubSectionWithoutAttemptAny = (
  targetSub: MultiSubjectSubSection,
  overrides: {
    subSectionMetaData: MultiSubjectSubSection['subSectionMetaData'];
    questionTypeAndMarks: MultiSubjectSubSection['questionTypeAndMarks'];
  }
): MultiSubjectSubSection =>
  new MultiSubjectSubSection({
    subSectionName: targetSub.subSectionName,
    subSectionTitle: targetSub.subSectionTitle,
    instructions: targetSub.instructions,
    ...(targetSub.subSectionNo !== undefined
      ? { subSectionNo: targetSub.subSectionNo }
      : {}),
    subSectionMetaData: overrides.subSectionMetaData,
    questionTypeAndMarks: overrides.questionTypeAndMarks,
    passageText: targetSub.passageText,
    passageImagesUpload: targetSub.passageImagesUpload,
    ...(targetSub.isValidate !== undefined
      ? { isValidate: targetSub.isValidate }
      : {}),
    ...(targetSub.subSectionType !== undefined
      ? { subSectionType: targetSub.subSectionType }
      : {}),
    ...(targetSub.isAiGeneratedPassage !== undefined
      ? { isAiGeneratedPassage: targetSub.isAiGeneratedPassage }
      : {}),
  });

/** Rebuild a row without optional groupQuestionAttemptedAny (protobuf-es skips undefined). */
const rebuildSubSectionQuestionWithoutGroupAttemptAny = (
  item: SubSectionQuestionType,
  overrides: Partial<{
    isGrouped: boolean;
    isAlternateEnable: boolean;
    noOfQuestion: number;
    marks: number;
    questionType: Question_QuestionEnum;
  }> = {}
): SubSectionQuestionType => {
  const isGrouped =
    overrides.isGrouped !== undefined ? overrides.isGrouped : item.isGrouped;
  const isAlternateEnable =
    overrides.isAlternateEnable !== undefined
      ? overrides.isAlternateEnable
      : item.isAlternateEnable;
  return new SubSectionQuestionType({
    questionType: overrides.questionType ?? item.questionType,
    noOfQuestion: overrides.noOfQuestion ?? item.noOfQuestion,
    ...(overrides.marks !== undefined
      ? { marks: overrides.marks }
      : item.marks !== undefined
      ? { marks: item.marks }
      : {}),
    isAlternateEnable: !!isAlternateEnable,
    ...(isGrouped ? { isGrouped: true } : {}),
    // Heading only applies while grouped; clear on ungroup.
    ...(isGrouped && item.groupQuestionHeading !== undefined
      ? { groupQuestionHeading: item.groupQuestionHeading }
      : {}),
  });
};

const styles: IStyles = {
  root: {
    boxSizing: 'border-box',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    borderRadius: { xs: pxToRem(15), md: pxTovW(30) },
    border: '2px solid #007CDC',
    backgroundColor: '#fff',
    padding: { xs: pxToRem(14), md: pxTovW(22) },
  },
  headerBox: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: 'space-between',
    alignItems: { xs: 'stretch', md: 'center' },
    gap: { xs: pxToRem(10), md: pxTovW(16) },
    flexWrap: { xs: 'nowrap', md: 'wrap' },
    marginBottom: { xs: pxToRem(12), md: pxTovW(16) },
  },
  headerTitleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(16) },
    width: { xs: '100%', md: 'auto' },
    flex: { md: 1 },
    minWidth: 0,
  },
  headerReorderBox: {
    display: 'flex',
    gap: { xs: pxToRem(10), md: pxTovW(20) },
    alignItems: 'center',
    minWidth: 0,
  },
  reorderIcon: {
    height: { xs: pxToRem(16), md: pxTovW(22) },
    width: { xs: pxToRem(12), md: pxTovW(14) },
    cursor: 'grab',
  },
  headerActionsBox: {
    display: 'flex',
    gap: { xs: pxToRem(10), md: pxTovW(16) },
    alignItems: 'center',
    flexWrap: 'wrap',
    width: { xs: '100%', md: 'auto' },
  },
  headerMarksPill: {
    backgroundColor: '#DCFAEF',
    borderRadius: { xs: pxToRem(8), md: pxTovW(8) },
    paddingX: { xs: pxToRem(12), md: pxTovW(15) },
    paddingY: { xs: pxToRem(10), md: pxTovW(5) },
    height: { xs: pxToRem(30), md: pxTovW(45) },
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
  },
  passageSectionBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(6), md: pxTovW(8) },
    cursor: 'pointer',
    backgroundColor: '#dcfaef',
    border: '1px solid #55ACFF',
    borderRadius: { xs: pxToRem(8), md: pxTovW(8) },
    paddingX: { xs: pxToRem(8), md: pxTovW(12) },
    paddingY: { xs: pxToRem(3), md: pxTovW(4) },
    height: { xs: pxToRem(30), md: pxTovW(45) },
    boxSizing: 'border-box',
  },
  passageSectionCheckbox: {
    padding: 0,
    color: '#55ACFF',
    '&.Mui-checked': {
      color: 'primary.main',
    },
  },
  groupQuestionsCheckboxIcon: {
    width: { xs: pxToRem(16), md: pxTovW(16) },
    height: { xs: pxToRem(16), md: pxTovW(16) },
    boxSizing: 'border-box',
    border: '1.5px solid',
    borderColor: 'primary.main',
    borderRadius: { xs: pxToRem(4), md: pxTovW(4) },
    backgroundColor: '#fff',
  },
  groupQuestionsCheckboxCheckedIcon: {
    width: { xs: pxToRem(16), md: pxTovW(16) },
    height: { xs: pxToRem(16), md: pxTovW(16) },
    boxSizing: 'border-box',
    borderRadius: { xs: pxToRem(4), md: pxTovW(4) },
    backgroundColor: 'primary.main',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  passageSectionCheckIcon: {
    fontSize: { xs: pxToRem(12), md: pxTovW(12) },
    color: '#fff',
  },
  passageSectionTypo: {
    width: 'max-content',
    color: '#1D1D1D',
    fontWeight: 400,
  },
  deleteIconMobile: {
    cursor: 'pointer',
    height: pxToRem(26),
    width: pxToRem(26),
    flexShrink: 0,
    display: { xs: 'block', md: 'none' },
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  },
  deleteIconDesktop: {
    cursor: 'pointer',
    height: { xs: pxToRem(20), md: pxTovW(32) },
    width: { xs: pxToRem(20), md: pxTovW(32) },
    flexShrink: 0,
    display: { xs: 'none', md: 'block' },
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  },
  questionRowBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: { xs: pxToRem(10), md: pxTovW(50) },
    width: '100%',
    boxSizing: 'border-box',
    border: { xs: '1px solid #D6E4F0', md: 'none' },
    borderRadius: { xs: pxToRem(12), md: 0 },
    padding: { xs: pxToRem(12), md: 0 },
    backgroundColor: { xs: '#FFFFFF', md: 'transparent' },
    marginBottom: { xs: pxToRem(10), md: 0 },
    boxShadow: {
      xs: '0px 2px 8px rgba(0, 67, 124, 0.12)',
      md: 'none',
    },
  },
  questionRowContentBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    flex: 1,
    minWidth: 0,
    width: { xs: '100%', md: 'auto' },
    marginTop: { xs: 0, md: pxTovW(16) },
    marginBottom: { xs: 0, md: pxTovW(16) },
  },
  questionRowHeaderBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: { xs: pxToRem(8), md: pxTovW(8) },
    width: '100%',
    marginBottom: { xs: pxToRem(10), md: pxTovW(8) },
  },
  questionSequenceBadge: {
    flexShrink: 0,
    backgroundColor: '#F9C80D',
    borderRadius: pxToRem(50),
    paddingX: { xs: pxToRem(10), md: pxTovW(12) },
    paddingY: { xs: pxToRem(3), md: pxTovW(4) },
    fontFamily: 'Poppins',
    fontWeight: 700,
    fontSize: { xs: pxToRem(11), md: pxTovW(13) },
    color: '#1D1D1D',
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
    alignSelf: 'flex-start',
  },
  rowDeleteIconMobile: {
    cursor: 'pointer',
    height: { xs: pxToRem(24), md: pxTovW(18) },
    width: { xs: pxToRem(24), md: pxTovW(18) },
    flexShrink: 0,
    display: { xs: 'block', md: 'none' },
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  },
  rowDeleteIconDesktop: {
    cursor: 'pointer',
    height: { xs: pxToRem(20), md: pxTovW(32) },
    width: { xs: pxToRem(20), md: pxTovW(32) },
    flexShrink: 0,
    display: { xs: 'none', md: 'block' },
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  },
  questionRowFieldsBox: {
    display: 'flex',
    alignItems: { xs: 'flex-start', md: 'flex-end' },
    gap: { xs: pxToRem(10), md: pxTovW(20) },
  },
  questionTypeAndDetailsBox: {
    flex: 1,
    minWidth: 0,
    width: '100%',
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: { xs: pxToRem(10), md: pxTovW(12) },
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', md: 'flex-end' },
    mt: 0,
  },
  questionDetailsBox: {
    width: { xs: '100%', md: 'auto' },
    flex: { md: 1 },
    minWidth: 0,
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: { xs: pxToRem(10), md: pxTovW(50), lg: pxTovW(50) },
    justifyContent: { xs: 'flex-start', md: 'flex-start' },
    alignItems: { xs: 'flex-start', md: 'flex-start' },
  },
  questionTypeBox: {
    width: { xs: '100%', md: 'unset' },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: { xs: pxToRem(6), md: pxTovW(8) },
  },
  selectMenuParentStyles: {
    width: { xs: '100%', md: 'unset' },
    height: {
      xs: pxToRem(36),
      md: pxTovW(42),
      lg: pxTovW(40),
    },
    lineHeight: 0,
  },
  selectMenuPlaceholderBox: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 'bold',
    textAlign: 'left',
    fontSize: { xs: pxToRem(12), md: pxTovW(13) },
    fontFamily: 'Poppins',
  },
  selectMenuValueBox: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 'bold',
    textAlign: 'left',
    fontSize: { xs: pxToRem(12), md: pxTovW(13) },
    fontFamily: 'Poppins',
  },
  selectMenuRootStyles: {
    height: { xs: pxToRem(36), md: pxTovW(42), lg: pxTovW(40) },
    minHeight: { xs: pxToRem(36), md: pxTovW(42), lg: pxTovW(40) },
    maxHeight: { xs: pxToRem(36), md: pxTovW(42), lg: pxTovW(40) },
    boxSizing: 'border-box',
    padding: 0,
    borderRadius: { xs: pxToRem(8), md: pxTovW(8) },
    overflow: 'hidden',
    background: '#F5FAFF',
    backgroundColor: '#F5FAFF',
    border: '1px solid #55ACFF',
    '.MuiOutlinedInput-notchedOutline': {
      border: 'none',
      top: 0,
      legend: {
        display: 'none',
      },
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:focus-visible .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.MuiInputBase-root': {
      height: { xs: pxToRem(36), md: pxTovW(42), lg: pxTovW(40) },
      minHeight: { xs: pxToRem(36), md: pxTovW(42), lg: pxTovW(40) },
      maxHeight: { xs: pxToRem(36), md: pxTovW(42), lg: pxTovW(40) },
      padding: 0,
      alignItems: 'center',
    },
    '& .MuiSelect-select, & .MuiOutlinedInput-input': {
      height: '100% !important',
      minHeight: 'unset !important',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      paddingTop: '0 !important',
      paddingBottom: '0 !important',
      paddingLeft: { xs: pxToRem(10), md: pxTovW(12) },
      paddingRight: { xs: pxToRem(28), md: pxTovW(32) },
    },
    '& .MuiSelect-select > em': {
      fontStyle: 'normal !important',
      fontFamily: 'Lato',
    },
    width: { xs: '100%', md: pxTovW(206), lg: pxTovW(250) },
  },
  marksAndNoOfQuestionsBox: {
    width: { xs: '100%', md: 'auto' },
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: { xs: pxToRem(10), md: pxTovW(50), lg: pxTovW(50) },
    justifyContent: 'flex-start',
    alignItems: { xs: 'unset', md: 'center' },
    flexShrink: 0,
  },
  marksAndQuestionsInnerRow: {
    width: { xs: '100%', md: 'auto' },
    display: 'flex',
    flexDirection: 'row',
    gap: { xs: pxToRem(12), md: pxTovW(50), lg: pxTovW(50) },
    justifyContent: { xs: 'flex-start', md: 'flex-start' },
    alignItems: { xs: 'flex-start', md: 'center' },
    flexShrink: 0,
  },
  marksBox: {
    flex: { xs: 1, md: 'unset' },
    width: { xs: 'auto', md: 'unset' },
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: { xs: pxToRem(6), md: pxTovW(8), lg: pxToRem(8) },
  },
  totalMarksBox: {
    // Mobile: own full-width centered row under marks + no. of questions
    width: { xs: '100%', md: 'unset' },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: { xs: 'flex-start', md: 'space-between' },
    gap: { xs: pxToRem(4), md: pxTovW(8), lg: pxToRem(8) },
    textAlign: 'center',
  },
  noOfQuestionsBox: {
    flex: { xs: 1, md: 'unset' },
    width: { xs: 'auto', md: 'unset' },
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: { xs: pxToRem(6), md: pxTovW(8) },
  },
  groupAndAlternateRowBox: {
    display: 'flex',
    flexWrap: { xs: 'nowrap', md: 'nowrap' },
    gap: { xs: pxToRem(16), md: pxTovW(50) },
    alignItems: 'center',
    marginTop: {
      xs: pxToRem(12),
      md: pxTovW(20),
      lg: pxTovW(15),
    },
    flexShrink: 0,
  },
  groupQuestionsBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(6), md: pxTovW(8) },
    cursor: 'pointer',
    marginTop: { xs: 0, md: pxTovW(4) },
  },
  groupAttemptAnyBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(6), md: pxTovW(8) },
    flexWrap: 'wrap',
    marginTop: { xs: pxToRem(10), md: pxTovW(10) },
  },
  groupAttemptAnyLabel: {
    width: 'max-content',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1,
    cursor: 'pointer',
  },
  groupAttemptAnyQuantityRoot: {
    width: { xs: pxToRem(96), md: pxTovW(110) },
  },
  alternateQuestionsCheckbox: {
    padding: 0,
    margin: 0,
    flexShrink: 0,
    color: 'primary.main',
    '&.Mui-checked': {
      color: 'primary.main',
    },
  },
  alternateQuestionsCheckboxIcon: {
    width: { xs: pxToRem(20), md: pxToRem(16), lg: pxTovW(24) },
    height: { xs: pxToRem(20), md: pxToRem(16), lg: pxTovW(24) },
    boxSizing: 'border-box',
    border: '1.5px solid',
    borderColor: 'primary.main',
    borderRadius: { xs: pxToRem(4), md: pxTovW(4) },
    backgroundColor: '#fff',
  },
  alternateQuestionsCheckboxCheckedIcon: {
    width: { xs: pxToRem(20), md: pxToRem(16), lg: pxTovW(24) },
    height: { xs: pxToRem(20), md: pxToRem(16), lg: pxTovW(24) },
    boxSizing: 'border-box',
    borderRadius: { xs: pxToRem(4), md: pxTovW(4) },
    backgroundColor: 'primary.main',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxCheckIcon: {
    fontSize: { xs: pxToRem(14), md: pxToRem(16) },
    color: '#fff',
  },
  alternateQuestionsTypo: {
    width: 'max-content',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1,
  },
  addAlternateQuestionsBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(6), md: pxTovW(12) },
    cursor: 'pointer',
    height: { xs: 'auto', md: pxTovW(40) },
  },
  rowDivider: {
    width: '100%',
    display: { xs: 'none', md: 'block' },
    borderColor: '#D6E4F0',
    borderBottomWidth: { xs: pxToRem(3), md: pxTovW(3) },
    marginY: { xs: pxToRem(8), md: pxTovW(10) },
  },
  addMoreTypo: {
    display: 'block',
    width: '100%',
    marginTop: { xs: pxToRem(10), md: pxTovW(20) },
    fontFamily: 'poppins',
    fontSize: { xs: pxToRem(14), md: pxTovW(15) },
    cursor: 'pointer',
    color: 'primary.main',
    opacity: 1,
  },
  addMoreTypoDisabled: {
    display: 'block',
    width: '100%',
    marginTop: { xs: pxToRem(10), md: pxTovW(20) },
    fontFamily: 'poppins',
    fontSize: { xs: pxToRem(14), md: pxTovW(15) },
    cursor: 'not-allowed',
    color: 'grey',
    opacity: 0.6,
  },
};

interface IOptionList {
  name: string;
  nonEditableItem?: boolean;
  marks?: number;
  noOfQuestions?: number;
  questionType?: Question_QuestionEnum;
}

interface ISectionProps {
  // index: number;
  // section: QuestionSection;
  updateSection: (index: number, section: QuestionSection) => void;
  deleteSection: (index: number) => void;

  subSectionDetails: SubSectionTypeV2;
  subSectionIndex: number;
  addMoreHandler: (subSectionIndex: number) => void;
}
export const QPGSubSection = (props: ISectionProps) => {
  const {
    // index,
    // section,
    updateSection,
    deleteSection,
    subSectionDetails,
    subSectionIndex,
    addMoreHandler,
  } = props;

  const MAX_MARKS_LIMIT = 20;
  const MAX_AI_QUESTIONS = 25;
  const MAX_NO_OF_QUESTIONS_LIMIT = 200;
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const dispatch = useDispatch();
  const { isOffline } = useDownloadContext();

  const {
    qpg_settings_config,
    current_qpg_template_info,
    qpg_subsection_data,
    qpg_content,
    qpg_selected_section_index,
    qpg_selected_chapter_data,
  } = deserify(useAppSelector((state) => state.assessment));

  const allowAiQuestions = !!qpg_settings_config?.allow_ai_questions;
  const remainingByType = allowAiQuestions
    ? getRemainingForSubSectionAIMode(
        qpg_subsection_data ?? [],
        subSectionIndex,
        MAX_AI_QUESTIONS
      )
    : getRemainingForSubSection(
        qpg_subsection_data ?? [],
        subSectionIndex,
        qpg_settings_config?.no_ofQuestions_by_types
      );
  const isPassageSection = isPassageSectionEnabled(subSectionDetails);
  const showHeaderMarksPill = allowAiQuestions && !isPassageSection;

  // ----------------------------------------------------

  const clampMarks = (marks: number, questionType: Question_QuestionEnum) => {
    if (questionType === Question_QuestionEnum.QUESTION_TYPE_UNDEFINED) {
      return 0;
    }
    return Math.min(Math.max(0, marks), MAX_MARKS_LIMIT);
  };

  // ----------------------------------------------------

  const getCurrentMarksForRow = (queDetails: SubSectionQuestionType) => {
    const marks =
      queDetails.marks ??
      qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === queDetails.questionType
      )?.marks ??
      0;
    return clampMarks(marks, queDetails.questionType);
  };

  // ----------------------------------------------------

  const subsectionTotalQuestions = getSubSectionQuestionTotal(
    subSectionDetails.subSectionQuestions
  );
  const isAttemptAny =
    allowAiQuestions && subSectionDetails.attemptAnyCount !== undefined;
  const marksPerQuestion = (() => {
    const firstDefinedRow = subSectionDetails.subSectionQuestions.find(
      (q) => q.questionType !== Question_QuestionEnum.QUESTION_TYPE_UNDEFINED
    );
    if (!firstDefinedRow) return 1;
    const rowMarks = getCurrentMarksForRow(firstDefinedRow);
    return rowMarks > 0 ? rowMarks : 1;
  })();
  const subsectionTotalMarks =
    getQpgCreationSubSectionMarksTotal(subSectionDetails);
  const subsectionTotalMarksDisplay = formatMarksDisplay(subsectionTotalMarks);

  // ----------------------------------------------------

  function getMaxQuestionCountByEnum(
    enumValue?: Question_QuestionEnum,
    currentRowIndex?: number
  ): number {
    if (!enumValue) {
      return 0;
    }
    if (allowAiQuestions && typeof currentRowIndex === 'number') {
      const usedInOtherRowsInThisSubsection =
        subSectionDetails.subSectionQuestions.reduce(
          (sum, q, i) =>
            i !== currentRowIndex && q.questionType === enumValue
              ? sum + (q.noOfQuestion ?? 0)
              : sum,
          0
        );
      return Math.max(0, MAX_AI_QUESTIONS - usedInOtherRowsInThisSubsection);
    }
    const subsectionTotalRemaining = remainingByType.get(enumValue);
    if (
      subsectionTotalRemaining !== undefined &&
      subsectionTotalRemaining !== Number.MAX_SAFE_INTEGER
    ) {
      if (typeof currentRowIndex === 'number') {
        const usedInOtherRowsInThisSubsection =
          subSectionDetails.subSectionQuestions.reduce(
            (sum, q, i) =>
              i !== currentRowIndex && q.questionType === enumValue
                ? sum + (q.noOfQuestion ?? 0)
                : sum,
            0
          );
        return Math.max(
          0,
          subsectionTotalRemaining - usedInOtherRowsInThisSubsection
        );
      }
      return subsectionTotalRemaining;
    }
    if (!questionTypeList.length) {
      return 0;
    }
    const questionTypeFound = questionTypeList.find(
      (e) => e.questionType === enumValue
    );
    return questionTypeFound?.noOfQuestions || 0;
  }

  // ----------------------------------------------------

  const getMaxNoOfQuestionsLimit = (
    enumValue?: Question_QuestionEnum,
    currentRowIndex?: number
  ): number => {
    const available = getMaxQuestionCountByEnum(enumValue, currentRowIndex);
    return Math.min(MAX_NO_OF_QUESTIONS_LIMIT, available);
  };

  // ----------------------------------------------------

  const noOfQuestions = new QuestionTypeCount(
    qpg_settings_config?.no_ofQuestions_by_types
  );
  const {
    noOfMcqs,
    noOfMcqm,
    noOfFib,
    noOfTf,
    noOfVeryShort,
    noOfShort,
    noOfLong,
    noOfCbq,
  } = noOfQuestions;

  // ----------------------------------------------------

  const getNoOfQuestions = (number?: number) => {
    return qpg_settings_config?.allow_ai_questions
      ? MAX_AI_QUESTIONS
      : number || 0;
  };

  // ----------------------------------------------------

  const questionTypeList: IOptionList[] = [
    {
      name: 'MCQ Single',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_MCQS
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_MCQS
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfMcqs),
      questionType: Question_QuestionEnum.QUESTION_TYPE_MCQS,
    },
    {
      name: 'MCQ Multiple',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_MCQM
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_MCQM
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfMcqm),
      questionType: Question_QuestionEnum.QUESTION_TYPE_MCQM,
    },
    {
      name: 'Fill in the Blanks',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_FIB
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_FIB
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfFib),
      questionType: Question_QuestionEnum.QUESTION_TYPE_FIB,
    },
    {
      name: 'True/False',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_TF
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_TF
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfTf),
      questionType: Question_QuestionEnum.QUESTION_TYPE_TF,
    },
    {
      name: 'Very Short',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfVeryShort),
      questionType: Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT,
    },
    {
      name: 'Short',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_SHORT
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_SHORT
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfShort),
      questionType: Question_QuestionEnum.QUESTION_TYPE_SHORT,
    },
    {
      name: 'Long Answer',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_LONG
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_LONG
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfLong),
      questionType: Question_QuestionEnum.QUESTION_TYPE_LONG,
    },
    {
      name: 'Competency Based',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_CBQ
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_CBQ
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfCbq),
      questionType: Question_QuestionEnum.QUESTION_TYPE_CBQ,
    },
  ].filter(
    (e) =>
      e.noOfQuestions &&
      e.noOfQuestions > 0 &&
      ((remainingByType.get(e.questionType!) ?? 0) > 0 ||
        subSectionDetails.subSectionQuestions.some(
          (q) => q.questionType === e.questionType
        ))
  );

  // ----------------------------------------------------

  const updateQuestionTypeMarks = (
    questionType: Question_QuestionEnum,
    marks: number
  ) => {
    const updatedQuestionTypeMarks = new QuestionTypeMarks({
      questionType: questionType,
      marks: marks,
    });
    const updatedQuestionTypeMarksList =
      qpg_settings_config?.question_type_marks?.filter(
        (e) => e.questionType !== questionType
      ) || [];
    updatedQuestionTypeMarksList.push(updatedQuestionTypeMarks);
    dispatch(
      setQPGSettingsConfig({
        ...qpg_settings_config,
        question_type_marks: updatedQuestionTypeMarksList,
      })
    );
  };

  // ----------------------------------------------------

  const updateQuestionTypeMarksForTypes = (
    questionTypes: Question_QuestionEnum[],
    marks: number
  ) => {
    const uniqueTypes = [
      ...new Set(
        questionTypes.filter(
          (type) => type !== Question_QuestionEnum.QUESTION_TYPE_UNDEFINED
        )
      ),
    ];
    if (!uniqueTypes.length) return;

    let updatedQuestionTypeMarksList = [
      ...(qpg_settings_config?.question_type_marks ?? []),
    ];
    uniqueTypes.forEach((questionType) => {
      updatedQuestionTypeMarksList = updatedQuestionTypeMarksList.filter(
        (e) => e.questionType !== questionType
      );
      updatedQuestionTypeMarksList.push(
        new QuestionTypeMarks({
          questionType,
          marks,
        })
      );
    });
    dispatch(
      setQPGSettingsConfig({
        ...qpg_settings_config,
        question_type_marks: updatedQuestionTypeMarksList,
      })
    );
  };

  // ----------------------------------------------------

  const syncQpgContentFromSubsectionData = (
    updatedSubSectionData: SubSectionTypeV2[]
  ) => {
    const sectionIndex = qpg_selected_section_index ?? 0;
    if (!qpg_content?.multiSubjectSections?.[sectionIndex]) return;
    const subsection = updatedSubSectionData[subSectionIndex];
    if (!subsection?.subSectionQuestions?.length) return;

    const sections = [...qpg_content.multiSubjectSections];
    const selectedSection = cloneObject(
      qpg_content.multiSubjectSections[sectionIndex]
    );
    const subSections = [...(selectedSection.multiSubjectSubSections ?? [])];
    const targetSub = subSections[subSectionIndex];
    if (!targetSub) return;

    const attemptAnyEnabled = subsection.attemptAnyCount !== undefined;
    const subSectionMetaData = subsection.subSectionQuestions.map((q, i) => {
      const existing = targetSub.subSectionMetaData?.[i];
      const { perChildMarks } = getEffectiveMarksForSubSectionQuestion({
        marks: q.marks ?? 0,
        noOfQuestion: q.noOfQuestion ?? 0,
        isGrouped: q.isGrouped,
        attemptAnyEnabled,
        groupQuestionAttemptedAny: q.groupQuestionAttemptedAny,
      });
      const groupedQuestions = (existing?.groupedQuestions ?? []).map(
        (child) =>
          new SectionQuestionMetaData({
            ...child,
            maxMarks: perChildMarks,
          })
      );
      return withSeededGroupedQuestionHeading(
        new SectionQuestionMetaData({
          questionId: existing?.questionId ?? '',
          alternateQuestions: existing?.alternateQuestions ?? [],
          sequence: existing?.sequence ?? i + 1,
          showMedia: existing?.showMedia ?? false,
          isAiGenerated: existing?.isAiGenerated ?? false,
          maxMarks: perChildMarks,
          groupedQuestions,
          ...(q.isGrouped !== undefined ? { isGrouped: q.isGrouped } : {}),
          ...(q.groupQuestionHeading !== undefined
            ? { groupQuestionHeading: q.groupQuestionHeading }
            : {}),
          ...(q.groupQuestionAttemptedAny !== undefined
            ? { groupQuestionAttemptedAny: q.groupQuestionAttemptedAny }
            : {}),
        }),
        q.questionType
      );
    });
    const questionTypeAndMarks = subsection.subSectionQuestions.map((q) => {
      const { perChildMarks } = getEffectiveMarksForSubSectionQuestion({
        marks: q.marks ?? 0,
        noOfQuestion: q.noOfQuestion ?? 0,
        isGrouped: q.isGrouped,
        attemptAnyEnabled,
        groupQuestionAttemptedAny: q.groupQuestionAttemptedAny,
      });
      return new QuestionTypeMarks({
        questionType:
          q.questionType ?? Question_QuestionEnum.QUESTION_TYPE_UNDEFINED,
        marks: toRequestMarks(perChildMarks),
      });
    });
    const updatedTargetSub =
      subsection.attemptAnyCount !== undefined
        ? new MultiSubjectSubSection({
            ...targetSub,
            subSectionMetaData,
            questionTypeAndMarks,
            attemptAnyCount: subsection.attemptAnyCount,
          })
        : rebuildContentSubSectionWithoutAttemptAny(targetSub, {
            subSectionMetaData,
            questionTypeAndMarks,
          });
    subSections[subSectionIndex] = updatedTargetSub;
    selectedSection.multiSubjectSubSections = subSections;
    sections[sectionIndex] = selectedSection;
    const updatedContent = cloneObject(qpg_content);
    updatedContent.multiSubjectSections = sections;
    dispatch(setQPGContent(updatedContent));
  };

  // ----------------------------------------------------

  // Group Questions and Add Alternate Questions cannot both be on.
  // Callers pass only the flag they changed. Omitted / false is treated as off
  const updateRowGroupOrAlternate = ({
    rowIndex,
    grouped,
    alternate,
  }: {
    rowIndex: number;
    grouped?: boolean;
    alternate?: boolean;
  }) => {
    const currentSubSectionData = qpg_subsection_data ?? [];
    const subsectionAtIdx = currentSubSectionData[subSectionIndex];
    if (!subsectionAtIdx) return;

    const updatedSubSectionQuestions = subsectionAtIdx.subSectionQuestions.map(
      (item, i) => {
        if (i !== rowIndex) return item;
        const nextGrouped = !!grouped;
        const nextAlternate = !!alternate;
        // Ungroup or switch to alternate → clear per-row group attempt-any
        if (!nextGrouped) {
          return rebuildSubSectionQuestionWithoutGroupAttemptAny(item, {
            isGrouped: false,
            isAlternateEnable: nextAlternate,
          });
        }
        const nextGroupAttempt =
          item.groupQuestionAttemptedAny !== undefined
            ? clampGroupQuestionAttemptedAny(
                item.groupQuestionAttemptedAny,
                item.noOfQuestion ?? 0
              )
            : undefined;
        if (nextGroupAttempt === undefined) {
          return rebuildSubSectionQuestionWithoutGroupAttemptAny(item, {
            isGrouped: true,
            isAlternateEnable: false,
          });
        }
        return new SubSectionQuestionType({
          ...item,
          isGrouped: true,
          isAlternateEnable: false,
          groupQuestionAttemptedAny: nextGroupAttempt,
        });
      }
    );
    const nextQuestionTotal = getSubSectionQuestionTotal(
      updatedSubSectionQuestions
    );
    const nextAttemptAnyCount =
      subsectionAtIdx.attemptAnyCount !== undefined
        ? clampAttemptAnyCount(
            subsectionAtIdx.attemptAnyCount,
            nextQuestionTotal
          )
        : undefined;
    const updatedSubSectionData = currentSubSectionData.map((sub, i) =>
      i === subSectionIndex
        ? withSubsectionAttemptAnyCount(
            sub,
            updatedSubSectionQuestions,
            nextAttemptAnyCount
          )
        : sub
    );
    dispatch(setQPGSubsectionData(updatedSubSectionData));
    syncQpgContentFromSubsectionData(updatedSubSectionData);
  };

  // ----------------------------------------------------

  const updateRowGroupQuestionAttemptedAny = (
    rowIndex: number,
    count: number | undefined
  ) => {
    const currentSubSectionData = qpg_subsection_data ?? [];
    const subsectionAtIdx = currentSubSectionData[subSectionIndex];
    if (!subsectionAtIdx) return;

    const updatedSubSectionQuestions = subsectionAtIdx.subSectionQuestions.map(
      (item, i) => {
        if (i !== rowIndex) return item;
        if (count === undefined) {
          return rebuildSubSectionQuestionWithoutGroupAttemptAny(item, {
            isGrouped: item.isGrouped,
            isAlternateEnable: item.isAlternateEnable,
          });
        }
        const nextGroupAttempt = clampGroupQuestionAttemptedAny(
          count,
          item.noOfQuestion ?? 0
        );
        if (nextGroupAttempt === undefined) {
          return rebuildSubSectionQuestionWithoutGroupAttemptAny(item, {
            isGrouped: true,
            isAlternateEnable: false,
          });
        }
        return new SubSectionQuestionType({
          ...item,
          isGrouped: true,
          isAlternateEnable: false,
          groupQuestionAttemptedAny: nextGroupAttempt,
        });
      }
    );
    const updatedSubSectionData = currentSubSectionData.map((sub, i) =>
      i === subSectionIndex
        ? new SubSectionTypeV2({
            ...sub,
            subSectionQuestions: updatedSubSectionQuestions,
          })
        : sub
    );
    dispatch(setQPGSubsectionData(updatedSubSectionData));
    syncQpgContentFromSubsectionData(updatedSubSectionData);
  };

  // ----------------------------------------------------

  const updateSubSectionRowNoOfQuestions = (
    rowIndex: number,
    nextCount: number
  ) => {
    const currentSubSectionData = qpg_subsection_data ?? [];
    const subsectionAtIdx = currentSubSectionData[subSectionIndex];
    if (!subsectionAtIdx) return;

    const updatedSubSectionQuestions = subsectionAtIdx.subSectionQuestions.map(
      (e, i) => {
        if (i !== rowIndex) return e;
        const nextGroupAttempt =
          e.groupQuestionAttemptedAny !== undefined
            ? clampGroupQuestionAttemptedAny(
                e.groupQuestionAttemptedAny,
                nextCount
              )
            : undefined;
        if (
          e.groupQuestionAttemptedAny !== undefined &&
          nextGroupAttempt === undefined
        ) {
          return rebuildSubSectionQuestionWithoutGroupAttemptAny(e, {
            noOfQuestion: nextCount,
            isGrouped: e.isGrouped,
            isAlternateEnable: e.isAlternateEnable,
          });
        }
        if (nextGroupAttempt !== undefined) {
          return new SubSectionQuestionType({
            ...e,
            noOfQuestion: nextCount,
            groupQuestionAttemptedAny: nextGroupAttempt,
          });
        }
        return new SubSectionQuestionType({
          ...e,
          noOfQuestion: nextCount,
        });
      }
    );
    const nextQuestionTotal = getSubSectionQuestionTotal(
      updatedSubSectionQuestions
    );
    const nextAttemptAnyCount =
      subsectionAtIdx.attemptAnyCount !== undefined
        ? clampAttemptAnyCount(
            subsectionAtIdx.attemptAnyCount,
            nextQuestionTotal
          )
        : undefined;
    const updatedSubSectionData = currentSubSectionData.map((sub, i) =>
      i === subSectionIndex
        ? withSubsectionAttemptAnyCount(
            sub,
            updatedSubSectionQuestions,
            nextAttemptAnyCount
          )
        : sub
    );
    dispatch(setQPGSubsectionData(updatedSubSectionData));
    syncQpgContentFromSubsectionData(updatedSubSectionData);
  };

  // ----------------------------------------------------

  const getMaxMarksLimit = (questionType: Question_QuestionEnum) =>
    questionType === Question_QuestionEnum.QUESTION_TYPE_UNDEFINED
      ? 0
      : MAX_MARKS_LIMIT;

  // ----------------------------------------------------

  const updateSubSectionRowMarks = (
    rowIndex: number,
    nextMarks: number,
    questionType: Question_QuestionEnum
  ) => {
    const clampedMarks = clampMarks(nextMarks, questionType);
    const currentSubSectionData = qpg_subsection_data ?? [];
    const subsectionAtIdx = currentSubSectionData[subSectionIndex];
    if (!subsectionAtIdx) return;

    const updatedSubSectionQuestions = subsectionAtIdx.subSectionQuestions.map(
      (e, i) =>
        i === rowIndex
          ? new SubSectionQuestionType({
              ...e,
              marks: clampedMarks,
            })
          : e
    );
    const updatedSubSectionData = currentSubSectionData.map((sub, i) =>
      i === subSectionIndex
        ? new SubSectionTypeV2({
            ...sub,
            subSectionQuestions: updatedSubSectionQuestions,
          })
        : sub
    );
    dispatch(setQPGSubsectionData(updatedSubSectionData));
    syncQpgContentFromSubsectionData(updatedSubSectionData);

    if (questionType !== Question_QuestionEnum.QUESTION_TYPE_UNDEFINED) {
      updateQuestionTypeMarks(questionType, clampedMarks);
    }
  };

  // ----------------------------------------------------

  const updateAllSubSectionMarks = (nextMarks: number) => {
    const currentSubSectionData = qpg_subsection_data ?? [];
    const subsectionAtIdx = currentSubSectionData[subSectionIndex];
    if (!subsectionAtIdx) return;

    const clampedMarks = Math.min(Math.max(1, nextMarks), MAX_MARKS_LIMIT);
    const updatedSubSectionQuestions = subsectionAtIdx.subSectionQuestions.map(
      (e) =>
        new SubSectionQuestionType({
          ...e,
          marks: clampedMarks,
        })
    );
    const updatedSubSectionData = currentSubSectionData.map((sub, i) =>
      i === subSectionIndex
        ? new SubSectionTypeV2({
            ...sub,
            subSectionQuestions: updatedSubSectionQuestions,
          })
        : sub
    );
    dispatch(setQPGSubsectionData(updatedSubSectionData));
    syncQpgContentFromSubsectionData(updatedSubSectionData);

    updateQuestionTypeMarksForTypes(
      updatedSubSectionQuestions.map((q) => q.questionType),
      clampedMarks
    );
  };

  // ----------------------------------------------------

  const syncPassageFieldsToQpgContent = (updatedSub: SubSectionTypeV2) => {
    const sectionIndex = qpg_selected_section_index ?? 0;
    if (!qpg_content?.multiSubjectSections?.[sectionIndex]) return;

    const sections = [...qpg_content.multiSubjectSections];
    const selectedSection = cloneObject(
      qpg_content.multiSubjectSections[sectionIndex]
    );
    const subSections = [...(selectedSection.multiSubjectSubSections ?? [])];
    const targetSub = subSections[subSectionIndex];
    if (!targetSub) return;

    // Rebuild instead of spreading so optional attemptAnyCount / subSectionType
    // are omitted when unset (protobuf-es initPartial skips undefined on a spread).
    subSections[subSectionIndex] = new MultiSubjectSubSection({
      subSectionName: targetSub.subSectionName,
      subSectionTitle: targetSub.subSectionTitle,
      instructions: targetSub.instructions,
      subSectionNo: targetSub.subSectionNo,
      subSectionMetaData: targetSub.subSectionMetaData,
      questionTypeAndMarks: targetSub.questionTypeAndMarks,
      passageText: updatedSub.passageText ?? [],
      passageImagesUpload: updatedSub.passageImagesUpload ?? [],
      isValidate: targetSub.isValidate,
      ...(updatedSub.isAiGeneratedPassage !== undefined
        ? { isAiGeneratedPassage: updatedSub.isAiGeneratedPassage }
        : {}),
      ...(updatedSub.attemptAnyCount !== undefined
        ? { attemptAnyCount: updatedSub.attemptAnyCount }
        : {}),
      ...(updatedSub.subSectionType !== undefined
        ? { subSectionType: updatedSub.subSectionType }
        : {}),
    });
    selectedSection.multiSubjectSubSections = subSections;
    sections[sectionIndex] = selectedSection;
    const updatedContent = cloneObject(qpg_content);
    updatedContent.multiSubjectSections = sections;
    dispatch(setQPGContent(updatedContent));
  };

  // ----------------------------------------------------

  const updatePassageSection = (checked: boolean) => {
    // Passage on/off is subSectionType === QPG_SUBSECTION_TYPE_PASSAGE.
    // Unchecking rebuilds without spreading so optional subSectionType and
    // attemptAnyCount are actually cleared (protobuf-es initPartial skips
    // undefined on a spread).
    const defaultPassageModuleIds = checked
      ? getAllPassageModuleIdsFromChapters(qpg_selected_chapter_data?.chapters)
      : [];
    const currentSubSectionData = qpg_subsection_data ?? [];
    const updatedSubSectionData = currentSubSectionData.map((sub, i) => {
      if (i !== subSectionIndex) return sub;
      if (checked) {
        return new SubSectionTypeV2({
          ...sub,
          subSectionType: QpgSubsectionTypeEnum.QPG_SUBSECTION_TYPE_PASSAGE,
          isAiGeneratedPassage: sub.isAiGeneratedPassage ?? false,
          passageModuleIds:
            sub.passageModuleIds?.length > 0
              ? sub.passageModuleIds
              : defaultPassageModuleIds,
        });
      }
      return new SubSectionTypeV2({
        subSectionName: sub.subSectionName,
        sectionNumber: sub.sectionNumber,
        subSectionQuestions: sub.subSectionQuestions,
        passageText: [],
        passageImagesUpload: [],
        passageModuleIds: [],
      });
    });
    dispatch(setQPGSubsectionData(updatedSubSectionData));
    const updatedSub = updatedSubSectionData[subSectionIndex];
    if (updatedSub) syncPassageFieldsToQpgContent(updatedSub);
  };

  // ----------------------------------------------------

  const updatePassageModuleIds = (moduleIds: number[]) => {
    const currentSubSectionData = qpg_subsection_data ?? [];
    const updatedSubSectionData = currentSubSectionData.map((sub, i) =>
      i === subSectionIndex
        ? new SubSectionTypeV2({
            ...sub,
            passageModuleIds: moduleIds,
          })
        : sub
    );
    dispatch(setQPGSubsectionData(updatedSubSectionData));
  };

  // ----------------------------------------------------

  const updatePassageText = (text: string) => {
    const currentSubSectionData = qpg_subsection_data ?? [];
    const updatedSubSectionData = currentSubSectionData.map((sub, i) =>
      i === subSectionIndex
        ? new SubSectionTypeV2({
            ...sub,
            passageText: [text],
          })
        : sub
    );
    dispatch(setQPGSubsectionData(updatedSubSectionData));
    const updatedSub = updatedSubSectionData[subSectionIndex];
    if (updatedSub) syncPassageFieldsToQpgContent(updatedSub);
  };

  // ----------------------------------------------------

  const updatePassageImages = (images: CloudUpload[]) => {
    const currentSubSectionData = qpg_subsection_data ?? [];
    const updatedSubSectionData = currentSubSectionData.map((sub, i) =>
      i === subSectionIndex
        ? new SubSectionTypeV2({
            ...sub,
            passageImagesUpload: images,
          })
        : sub
    );
    dispatch(setQPGSubsectionData(updatedSubSectionData));
    const updatedSub = updatedSubSectionData[subSectionIndex];
    if (updatedSub) syncPassageFieldsToQpgContent(updatedSub);
  };

  // ----------------------------------------------------

  const updateIsAiGeneratedPassage = (isAiGenerated: boolean) => {
    const currentSubSectionData = qpg_subsection_data ?? [];
    const updatedSubSectionData = currentSubSectionData.map((sub, i) =>
      i === subSectionIndex
        ? new SubSectionTypeV2({
            ...sub,
            isAiGeneratedPassage: isAiGenerated,
          })
        : sub
    );
    dispatch(setQPGSubsectionData(updatedSubSectionData));
    const updatedSub = updatedSubSectionData[subSectionIndex];
    if (updatedSub) syncPassageFieldsToQpgContent(updatedSub);
  };

  // ----------------------------------------------------

  const updateAttemptAnyCount = (count: number | undefined) => {
    const currentSubSectionData = qpg_subsection_data ?? [];
    const currentSub = currentSubSectionData[subSectionIndex];
    const questionTotal = getSubSectionQuestionTotal(
      currentSub?.subSectionQuestions ?? []
    );
    const wasAttemptAny = currentSub?.attemptAnyCount !== undefined;
    const nextCount =
      count === undefined
        ? undefined
        : clampAttemptAnyCount(count, questionTotal);
    const shouldEqualizeMarks = nextCount !== undefined && !wasAttemptAny;
    const equalizedMarks = shouldEqualizeMarks
      ? (() => {
          const firstDefinedRow = currentSub?.subSectionQuestions.find(
            (q) =>
              q.questionType !== Question_QuestionEnum.QUESTION_TYPE_UNDEFINED
          );
          if (!firstDefinedRow) return 1;
          const rowMarks = getCurrentMarksForRow(firstDefinedRow);
          return rowMarks > 0 ? rowMarks : 1;
        })()
      : undefined;
    const updatedSubSectionData = currentSubSectionData.map((sub, i) => {
      if (i !== subSectionIndex) return sub;
      const nextQuestions =
        equalizedMarks !== undefined
          ? sub.subSectionQuestions.map(
              (e) =>
                new SubSectionQuestionType({
                  ...e,
                  marks: equalizedMarks,
                })
            )
          : sub.subSectionQuestions;
      return withSubsectionAttemptAnyCount(sub, nextQuestions, nextCount);
    });
    dispatch(setQPGSubsectionData(updatedSubSectionData));
    const updatedSub = updatedSubSectionData[subSectionIndex];
    if (updatedSub) {
      syncPassageFieldsToQpgContent(updatedSub);
      if (equalizedMarks !== undefined) {
        syncQpgContentFromSubsectionData(updatedSubSectionData);
        updateQuestionTypeMarksForTypes(
          updatedSub.subSectionQuestions.map((q) => q.questionType),
          equalizedMarks
        );
      }
    }
  };

  // ----------------------------------------------------

  const deleteRowHandler = (rowIndex: number) => {
    const currentSubSectionData = qpg_subsection_data ?? [];
    const subsectionAtIdx = currentSubSectionData[subSectionIndex];
    if (!subsectionAtIdx?.subSectionQuestions?.length) return;

    const updatedSubSectionQuestions =
      subsectionAtIdx.subSectionQuestions.filter((_, i) => i !== rowIndex);
    const nextQuestionTotal = getSubSectionQuestionTotal(
      updatedSubSectionQuestions
    );
    const nextAttemptAnyCount =
      subsectionAtIdx.attemptAnyCount !== undefined
        ? clampAttemptAnyCount(
            subsectionAtIdx.attemptAnyCount,
            nextQuestionTotal
          )
        : undefined;
    const updatedSubSectionData = currentSubSectionData.map((sub, i) =>
      i === subSectionIndex
        ? withSubsectionAttemptAnyCount(
            sub,
            updatedSubSectionQuestions,
            nextAttemptAnyCount
          )
        : sub
    );
    dispatch(setQPGSubsectionData(updatedSubSectionData));

    const sectionIndex = qpg_selected_section_index ?? 0;
    if (qpg_content?.multiSubjectSections?.[sectionIndex]) {
      const sections = [...qpg_content.multiSubjectSections];
      const selectedSection = cloneObject(
        qpg_content.multiSubjectSections[sectionIndex]
      );
      const subSections = [...(selectedSection.multiSubjectSubSections ?? [])];
      const targetSub = subSections[subSectionIndex];
      if (targetSub) {
        const newMetaData = (targetSub.subSectionMetaData ?? []).filter(
          (_, i) => i !== rowIndex
        );
        const newQuestionTypeAndMarks = (
          targetSub.questionTypeAndMarks ?? []
        ).filter((_, i) => i !== rowIndex);
        subSections[subSectionIndex] =
          nextAttemptAnyCount !== undefined
            ? new MultiSubjectSubSection({
                ...targetSub,
                subSectionMetaData: newMetaData,
                questionTypeAndMarks: newQuestionTypeAndMarks,
                attemptAnyCount: nextAttemptAnyCount,
              })
            : rebuildContentSubSectionWithoutAttemptAny(targetSub, {
                subSectionMetaData: newMetaData,
                questionTypeAndMarks: newQuestionTypeAndMarks,
              });
        selectedSection.multiSubjectSubSections = subSections;
        sections[sectionIndex] = selectedSection;
        const updatedContent = cloneObject(qpg_content);
        updatedContent.multiSubjectSections = sections;
        dispatch(setQPGContent(updatedContent));
      }
    }
  };

  // ----------------------------------------------------

  const canAddMoreQuestionType = () => {
    if (!areAllSubSectionQuestionsFilled(subSectionDetails.subSectionQuestions))
      return false;
    if (allowAiQuestions) {
      return questionTypeList.some(
        (e) => (remainingByType.get(e.questionType!) ?? 0) > 0
      );
    }
    const selectedTypes = new Set(
      subSectionDetails.subSectionQuestions
        .map((q) => q.questionType)
        .filter(
          (t): t is Question_QuestionEnum =>
            t !== undefined &&
            t !== Question_QuestionEnum.QUESTION_TYPE_UNDEFINED
        )
    );
    return selectedTypes.size < questionTypeList.length;
  };

  // ----------------------------------------------------

  /* Question types already selected in other rows of this subsection
   (so we can disable them in the dropdown) */
  const getQuestionTypesUsedByOtherRows = (
    currentRowIndex: number
  ): Set<Question_QuestionEnum> =>
    new Set(
      subSectionDetails.subSectionQuestions
        .map((q, i) => (i !== currentRowIndex ? q.questionType : undefined))
        .filter(
          (t): t is Question_QuestionEnum =>
            t !== undefined &&
            t !== Question_QuestionEnum.QUESTION_TYPE_UNDEFINED
        )
    );

  // ----------------------------------------------------

  const getRowSequenceContribution = (
    subsectionIdx: number,
    rowIndex: number,
    count: number
  ) => {
    if (count <= 0) return 0;
    return qpg_subsection_data?.[subsectionIdx]?.subSectionQuestions[rowIndex]
      ?.isGrouped
      ? 1
      : count;
  };

  // ----------------------------------------------------

  const getQuestionSequenceRange = (
    rowIndex: number
  ): { start: number; end: number } | null => {
    const count =
      subSectionDetails.subSectionQuestions[rowIndex]?.noOfQuestion ?? 0;
    if (count <= 0) return null;

    const contribution = getRowSequenceContribution(
      subSectionIndex,
      rowIndex,
      count
    );

    const priorFromOtherSubsections = (qpg_subsection_data ?? [])
      .slice(0, subSectionIndex)
      .reduce(
        (sum, sub, subsectionIdx) =>
          sum +
          sub.subSectionQuestions.reduce(
            (rowSum, q, rowIdx) =>
              rowSum +
              getRowSequenceContribution(
                subsectionIdx,
                rowIdx,
                q.noOfQuestion ?? 0
              ),
            0
          ),
        0
      );
    const priorFromThisSubsection = subSectionDetails.subSectionQuestions
      .slice(0, rowIndex)
      .reduce(
        (sum, q, i) =>
          sum +
          getRowSequenceContribution(subSectionIndex, i, q.noOfQuestion ?? 0),
        0
      );
    const start = priorFromOtherSubsections + priorFromThisSubsection + 1;
    return { start, end: start + contribution - 1 };
  };

  const subsectionQuestionRange = (() => {
    let start: number | undefined;
    let end: number | undefined;
    subSectionDetails.subSectionQuestions.forEach((_, index) => {
      const range = getQuestionSequenceRange(index);
      if (!range) return;
      if (start === undefined) start = range.start;
      end = range.end;
    });
    return {
      start: start ?? 1,
      end: end ?? start ?? 1,
    };
  })();

  // ----------------------------------------------------

  return (
    <Box sx={styles.root}>
      <Box sx={styles.headerBox}>
        <Box sx={styles.headerTitleRow}>
          <Box sx={styles.headerReorderBox}>
            <IconWrapper
              name="reorder"
              type="png"
              parentFolder="icons"
              customSx={styles.reorderIcon}
            />
            <Typography variant="h3" fontWeight="bold">
              {subSectionDetails.subSectionName}
            </Typography>
          </Box>
          <IconWrapper
            name="delete2"
            type="png"
            parentFolder="icons"
            onClick={async () => {
              deleteSection(subSectionIndex);
            }}
            customSx={styles.deleteIconMobile}
          />
        </Box>

        <Box sx={styles.headerActionsBox}>
          {showHeaderMarksPill ? (
            <Box sx={styles.headerMarksPill}>
              <Typography variant="h3" fontWeight={'bold'}>
                {subsectionTotalMarksDisplay === '1'
                  ? '1 Mark'
                  : `${subsectionTotalMarksDisplay} Marks`}
              </Typography>
            </Box>
          ) : null}
          {allowAiQuestions ? (
            <Box
              sx={styles.passageSectionBox}
              onClick={() => updatePassageSection(!isPassageSection)}
            >
              <Checkbox
                checked={isPassageSection}
                onChange={(e) => updatePassageSection(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                disableRipple
                sx={styles.passageSectionCheckbox}
                icon={<Box sx={styles.groupQuestionsCheckboxIcon} />}
                checkedIcon={
                  <Box sx={styles.groupQuestionsCheckboxCheckedIcon}>
                    <CheckIcon sx={styles.passageSectionCheckIcon} />
                  </Box>
                }
              />
              <Typography variant="h5" sx={styles.passageSectionTypo}>
                Passage / Comprehension Section
              </Typography>
            </Box>
          ) : null}
          <IconWrapper
            name="delete2"
            type="png"
            parentFolder="icons"
            onClick={async () => {
              deleteSection(subSectionIndex);
            }}
            customSx={styles.deleteIconDesktop}
          />
        </Box>
      </Box>

      {allowAiQuestions && isPassageSection ? (
        <QPGPassageSectionPanel
          sectionMarks={subsectionTotalMarks}
          passageText={subSectionDetails.passageText?.[0] ?? ''}
          attemptAnyCount={subSectionDetails.attemptAnyCount}
          maxAttemptAnyCount={Math.max(subsectionTotalQuestions - 1, 0)}
          marksPerQuestion={marksPerQuestion}
          maxMarksPerQuestion={MAX_MARKS_LIMIT}
          questionRangeStart={subsectionQuestionRange.start}
          questionRangeEnd={subsectionQuestionRange.end}
          passageImagesUpload={subSectionDetails.passageImagesUpload ?? []}
          isAiGeneratedPassage={subSectionDetails.isAiGeneratedPassage ?? false}
          passageModuleIds={subSectionDetails.passageModuleIds ?? []}
          onPassageTextChange={updatePassageText}
          onAttemptAnyCountChange={updateAttemptAnyCount}
          onMarksPerQuestionChange={updateAllSubSectionMarks}
          onPassageImagesChange={updatePassageImages}
          onIsAiGeneratedPassageChange={updateIsAiGeneratedPassage}
          onPassageModuleIdsChange={updatePassageModuleIds}
        />
      ) : null}

      {subSectionDetails.subSectionQuestions.map((queDetails, index) => {
        const sequenceRange = getQuestionSequenceRange(index);
        const groupQuestionCount = queDetails.noOfQuestion ?? 0;
        const canEnableGroupAttemptAny = groupQuestionCount >= 2;
        const groupAttemptAnyMax = Math.max(groupQuestionCount - 1, 1);
        return (
          <Fragment key={index}>
            <Box sx={styles.questionRowBox}>
              <Box sx={styles.questionRowContentBox}>
                {sequenceRange ||
                subSectionDetails.subSectionQuestions.length > 1 ? (
                  <Box
                    sx={{
                      ...styles.questionRowHeaderBox,
                      ...(!sequenceRange
                        ? { display: { xs: 'flex', md: 'none' } }
                        : {}),
                    }}
                  >
                    {sequenceRange ? (
                      <Typography
                        component="span"
                        sx={styles.questionSequenceBadge}
                      >
                        {sequenceRange.start === sequenceRange.end
                          ? `Q. ${sequenceRange.start}`
                          : `Qs. ${sequenceRange.start} - ${sequenceRange.end}`}
                      </Typography>
                    ) : (
                      <Box />
                    )}
                    {subSectionDetails.subSectionQuestions.length > 1 ? (
                      <IconWrapper
                        name="delete-with-circle"
                        type="png"
                        parentFolder="icons"
                        onClick={() => deleteRowHandler(index)}
                        customSx={styles.rowDeleteIconMobile}
                      />
                    ) : null}
                  </Box>
                ) : null}
                <Box sx={styles.questionRowFieldsBox}>
                  <Box sx={styles.questionTypeAndDetailsBox}>
                    <Box sx={styles.questionDetailsBox}>
                      <Box sx={styles.questionTypeBox}>
                        <Typography variant="h5" fontWeight="bold">
                          Choose Question Type
                        </Typography>
                        <SelectMenu
                          parentStyles={styles.selectMenuParentStyles}
                          placeholder="Select Question Type"
                          value={
                            questionTypeList.find(
                              (e) => e.questionType === queDetails.questionType
                            )?.name || ''
                          }
                          renderValue={(selected, placeholder) => {
                            const questionTypeFound = questionTypeList.find(
                              (e) => e.name === selected
                            );
                            return !questionTypeFound ? (
                              <Box sx={styles.selectMenuPlaceholderBox}>
                                {placeholder}
                              </Box>
                            ) : (
                              <Box sx={styles.selectMenuValueBox}>
                                {questionTypeFound.name}
                                {/* <Typography
                                  variant="cardText"
                                  sx={{
                                    color: '#0078D5',
                                    pl: { xs: pxToRem(6), md: pxTovW(6) },
                                  }}
                                >
                                  {questionTypeFound.marks} Marks
                                </Typography> */}
                              </Box>
                            );
                          }}
                          optionList={questionTypeList.map((e, optionIndex) => {
                            const usedByOtherRows =
                              getQuestionTypesUsedByOtherRows(index);
                            const remaining = remainingByType.get(
                              e.questionType!
                            );
                            const exhaustedAndNotSelected =
                              remaining === 0 &&
                              queDetails.questionType !== e.questionType;
                            return {
                              name: e.name,
                              id: optionIndex + 1,
                              nonEditableItem:
                                e.nonEditableItem ||
                                usedByOtherRows.has(e.questionType!) ||
                                exhaustedAndNotSelected,
                            };
                          })}
                          onChange={async (value: string) => {
                            const questionTypeFound = questionTypeList.find(
                              (e) => e.name === value
                            );
                            // if (questionTypeFound) {
                            //   const updatedQuestionSection = new QuestionSection({
                            //     ...section,
                            //     noOfQuestion: 0,
                            //     marks: questionTypeFound.marks || 0,
                            //     questionType:
                            //       questionTypeFound.questionType ||
                            //       Question_QuestionEnum.QUESTION_TYPE_UNDEFINED,
                            //   });
                            //   updateSection(index, updatedQuestionSection);
                            // }

                            const currentSubSectionData =
                              qpg_subsection_data ?? [];
                            const subsectionAtIdx =
                              currentSubSectionData[subSectionIndex];
                            if (subsectionAtIdx) {
                              const nextQuestionType =
                                questionTypeFound?.questionType ??
                                Question_QuestionEnum.QUESTION_TYPE_UNDEFINED;
                              const updatedSubSectionQuestions =
                                subsectionAtIdx.subSectionQuestions.map(
                                  (e, i) => {
                                    if (i !== index) return e;
                                    const marksOverride =
                                      subsectionAtIdx.attemptAnyCount !==
                                      undefined
                                        ? { marks: marksPerQuestion }
                                        : {};
                                    if (
                                      e.groupQuestionAttemptedAny !== undefined
                                    ) {
                                      return rebuildSubSectionQuestionWithoutGroupAttemptAny(
                                        e,
                                        {
                                          questionType: nextQuestionType,
                                          noOfQuestion: 0,
                                          isGrouped: e.isGrouped,
                                          isAlternateEnable:
                                            e.isAlternateEnable,
                                          ...marksOverride,
                                        }
                                      );
                                    }
                                    return new SubSectionQuestionType({
                                      ...e,
                                      questionType: nextQuestionType,
                                      noOfQuestion: 0,
                                      ...marksOverride,
                                    });
                                  }
                                );
                              const nextQuestionTotal =
                                getSubSectionQuestionTotal(
                                  updatedSubSectionQuestions
                                );
                              const nextAttemptAnyCount =
                                subsectionAtIdx.attemptAnyCount !== undefined
                                  ? clampAttemptAnyCount(
                                      subsectionAtIdx.attemptAnyCount,
                                      nextQuestionTotal
                                    )
                                  : undefined;
                              const updatedSubSectionData =
                                currentSubSectionData.map((sub, i) =>
                                  i === subSectionIndex
                                    ? withSubsectionAttemptAnyCount(
                                        sub,
                                        updatedSubSectionQuestions,
                                        nextAttemptAnyCount
                                      )
                                    : sub
                                );
                              dispatch(
                                setQPGSubsectionData(updatedSubSectionData)
                              );
                              syncQpgContentFromSubsectionData(
                                updatedSubSectionData
                              );
                              if (
                                nextAttemptAnyCount !== undefined &&
                                nextQuestionType !==
                                  Question_QuestionEnum.QUESTION_TYPE_UNDEFINED
                              ) {
                                updateQuestionTypeMarks(
                                  nextQuestionType,
                                  marksPerQuestion
                                );
                              }
                            }
                          }}
                          rootStyles={styles.selectMenuRootStyles}
                        />
                      </Box>

                      <Box sx={styles.marksAndNoOfQuestionsBox}>
                        <Box sx={styles.marksAndQuestionsInnerRow}>
                          <Box sx={styles.marksBox}>
                            {isAttemptAny ? (
                              <>
                                <Typography
                                  variant="h5"
                                  fontWeight="bold"
                                  marginRight={'auto'}
                                  width={'max-content'}
                                >
                                  {queDetails.isGrouped
                                    ? 'Marks per sub question'
                                    : 'Marks per question'}
                                </Typography>
                                <Typography variant="h5" fontWeight="bold">
                                  {formatMarksDisplay(
                                    queDetails.isGrouped
                                      ? marksPerQuestion /
                                          Math.max(
                                            1,
                                            queDetails.groupQuestionAttemptedAny !==
                                              undefined
                                              ? queDetails.groupQuestionAttemptedAny
                                              : queDetails.noOfQuestion ?? 1
                                          )
                                      : marksPerQuestion
                                  )}
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Typography
                                  variant="h5"
                                  fontWeight="bold"
                                  marginRight={'auto'}
                                  width={'max-content'}
                                >
                                  {queDetails.isGrouped
                                    ? 'Marks per sub question'
                                    : 'Marks per question'}
                                </Typography>
                                <QuantityButtonV2
                                  size="compact"
                                  // highlightWhenFilled
                                  value={getCurrentMarksForRow(queDetails)}
                                  max={getMaxMarksLimit(
                                    queDetails.questionType
                                  )}
                                  disabled={
                                    getMaxMarksLimit(
                                      queDetails.questionType
                                    ) === 0
                                  }
                                  onChange={(nextValue: number) => {
                                    updateSubSectionRowMarks(
                                      index,
                                      nextValue,
                                      queDetails.questionType
                                    );
                                  }}
                                  onIncrement={() => {
                                    const currentMarks =
                                      getCurrentMarksForRow(queDetails);
                                    if (currentMarks < MAX_MARKS_LIMIT) {
                                      updateSubSectionRowMarks(
                                        index,
                                        currentMarks + 1,
                                        queDetails.questionType
                                      );
                                    }
                                  }}
                                  onDecrement={() => {
                                    const currentMarks =
                                      getCurrentMarksForRow(queDetails);
                                    if (currentMarks > 0) {
                                      updateSubSectionRowMarks(
                                        index,
                                        Math.max(0, currentMarks - 1),
                                        queDetails.questionType
                                      );
                                    }
                                  }}
                                />
                              </>
                            )}
                          </Box>

                          <Box sx={styles.noOfQuestionsBox}>
                            <Typography
                              variant="h5"
                              fontWeight="bold"
                              marginRight={'auto'}
                              width={'max-content'}
                            >
                              No. of Questions
                            </Typography>
                            <QuantityButtonV2
                              size="compact"
                              // highlightWhenFilled
                              value={queDetails.noOfQuestion ?? 0}
                              max={getMaxNoOfQuestionsLimit(
                                queDetails.questionType,
                                index
                              )}
                              disabled={
                                getMaxNoOfQuestionsLimit(
                                  queDetails.questionType,
                                  index
                                ) === 0
                              }
                              onChange={(nextValue: number) => {
                                updateSubSectionRowNoOfQuestions(
                                  index,
                                  nextValue
                                );
                              }}
                              onIncrement={() => {
                                const maxCount = getMaxNoOfQuestionsLimit(
                                  queDetails.questionType,
                                  index
                                );
                                const currentCount =
                                  queDetails.noOfQuestion ?? 0;
                                if (currentCount < maxCount) {
                                  updateSubSectionRowNoOfQuestions(
                                    index,
                                    currentCount + 1
                                  );
                                }
                              }}
                              onDecrement={() => {
                                const currentCount =
                                  queDetails.noOfQuestion ?? 0;
                                if (currentCount > 0) {
                                  updateSubSectionRowNoOfQuestions(
                                    index,
                                    currentCount - 1
                                  );
                                }
                              }}
                            />
                          </Box>
                        </Box>

                        <Box sx={styles.totalMarksBox}>
                          <Typography variant="h5" fontWeight="bold">
                            Total Marks
                          </Typography>
                          <Typography variant="h5" fontWeight="bold">
                            {formatMarksDisplay(
                              isAttemptAny && queDetails.isGrouped
                                ? marksPerQuestion
                                : queDetails.isGrouped &&
                                  queDetails.groupQuestionAttemptedAny !==
                                    undefined
                                  ? Math.max(
                                      1,
                                      queDetails.groupQuestionAttemptedAny
                                    ) * getCurrentMarksForRow(queDetails)
                                  : (queDetails.noOfQuestion ?? 0) *
                                    (isAttemptAny
                                      ? marksPerQuestion
                                      : getCurrentMarksForRow(queDetails))
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box sx={styles.groupAndAlternateRowBox}>
                  <Box
                    sx={styles.groupQuestionsBox}
                    onClick={() =>
                      updateRowGroupOrAlternate({
                        rowIndex: index,
                        grouped: !queDetails.isGrouped,
                      })
                    }
                  >
                    <Checkbox
                      checked={!!queDetails.isGrouped}
                      onChange={(e) =>
                        updateRowGroupOrAlternate({
                          rowIndex: index,
                          grouped: e.target.checked,
                        })
                      }
                      onClick={(e) => e.stopPropagation()}
                      disableRipple
                      sx={styles.alternateQuestionsCheckbox}
                      color="primary"
                      icon={<Box sx={styles.alternateQuestionsCheckboxIcon} />}
                      checkedIcon={
                        <Box sx={styles.alternateQuestionsCheckboxCheckedIcon}>
                          <CheckIcon sx={styles.checkboxCheckIcon} />
                        </Box>
                      }
                    />
                    <Typography variant="h5" sx={styles.alternateQuestionsTypo}>
                      Group Questions
                    </Typography>
                  </Box>

                  <Box sx={styles.addAlternateQuestionsBox}>
                    <Checkbox
                      checked={queDetails.isAlternateEnable}
                      onChange={(e) =>
                        updateRowGroupOrAlternate({
                          rowIndex: index,
                          alternate: e.target.checked,
                        })
                      }
                      sx={styles.alternateQuestionsCheckbox}
                      color="primary"
                      disableRipple
                      icon={<Box sx={styles.alternateQuestionsCheckboxIcon} />}
                      checkedIcon={
                        <Box sx={styles.alternateQuestionsCheckboxCheckedIcon}>
                          <CheckIcon sx={styles.checkboxCheckIcon} />
                        </Box>
                      }
                    />
                    <Typography variant="h5" sx={styles.alternateQuestionsTypo}>
                      Add Alternate Questions
                    </Typography>
                  </Box>
                </Box>

                {queDetails.isGrouped ? (
                  <Box sx={styles.groupAttemptAnyBox}>
                    <Checkbox
                      checked={
                        canEnableGroupAttemptAny &&
                        queDetails.groupQuestionAttemptedAny !== undefined
                      }
                      disabled={!canEnableGroupAttemptAny}
                      onChange={(e) => {
                        if (!canEnableGroupAttemptAny) return;
                        updateRowGroupQuestionAttemptedAny(
                          index,
                          e.target.checked ? 1 : undefined
                        );
                      }}
                      disableRipple
                      sx={{
                        ...styles.alternateQuestionsCheckbox,
                        ...(!canEnableGroupAttemptAny ? { opacity: 0.45 } : {}),
                      }}
                      color="primary"
                      icon={<Box sx={styles.alternateQuestionsCheckboxIcon} />}
                      checkedIcon={
                        <Box sx={styles.alternateQuestionsCheckboxCheckedIcon}>
                          <CheckIcon sx={styles.checkboxCheckIcon} />
                        </Box>
                      }
                    />
                    <Typography
                      variant="h5"
                      sx={{
                        ...styles.groupAttemptAnyLabel,
                        ...(!canEnableGroupAttemptAny
                          ? { cursor: 'default', opacity: 0.45 }
                          : {}),
                      }}
                      onClick={() => {
                        if (!canEnableGroupAttemptAny) return;
                        updateRowGroupQuestionAttemptedAny(
                          index,
                          queDetails.groupQuestionAttemptedAny !== undefined
                            ? undefined
                            : 1
                        );
                      }}
                    >
                      Attempt any (in group)
                    </Typography>
                    {canEnableGroupAttemptAny &&
                    queDetails.groupQuestionAttemptedAny !== undefined ? (
                      <QuantityButtonV2
                        size="compact"
                        showStepperWhenZero
                        value={Math.min(
                          Math.max(
                            queDetails.groupQuestionAttemptedAny ?? 1,
                            1
                          ),
                          groupAttemptAnyMax
                        )}
                        min={1}
                        max={groupAttemptAnyMax}
                        onChange={(nextValue) =>
                          updateRowGroupQuestionAttemptedAny(index, nextValue)
                        }
                        rootStyles={styles.groupAttemptAnyQuantityRoot}
                      />
                    ) : null}
                  </Box>
                ) : null}
              </Box>

              {subSectionDetails.subSectionQuestions.length > 1 && (
                <IconWrapper
                  name="delete-with-circle"
                  type="png"
                  parentFolder="icons"
                  onClick={() => deleteRowHandler(index)}
                  customSx={styles.rowDeleteIconDesktop}
                />
              )}
            </Box>
            {index < subSectionDetails.subSectionQuestions.length - 1 ? (
              <Divider sx={styles.rowDivider} />
            ) : null}
          </Fragment>
        );
      })}

      <Typography
        variant="linkText"
        color="primary"
        textAlign="center"
        onClick={() => {
          if (canAddMoreQuestionType()) {
            addMoreHandler(subSectionIndex);
          }
        }}
        sx={
          canAddMoreQuestionType()
            ? styles.addMoreTypo
            : styles.addMoreTypoDisabled
        }
      >
        + Add More
      </Typography>
    </Box>
  );
};

// ==========================================================
